import { useMembershipPausedReport } from "@gymflow/api";
import { PARAMETER_DATE_FORMAT_WITHOUT_TZ } from "@gymflow/common";
import { ReportMemberDTO } from "@gymflow/types";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import moment from "moment-timezone";
import qs from "qs";
import { useState } from "react";
import { useHistory, useLocation } from "react-router";

import { LinkButton } from "../../components/atoms";
import { ReportPagination } from "../../components/molecules";
import { Table } from "../../components/organisms";
import { Report } from "../../components/organisms/Report";
import { usePortalRoutes } from "../../hooks/usePortalRoutes";
import { useClubSettings } from "../../providers";
import useGymflowModels from "../../store";
import { UserMemberPage } from "../UserMember";

export function MembershipPaused() {
  const { createMemberLink } = usePortalRoutes();
  const history = useHistory();
  const { api } = useGymflowModels();

  const {
     club, properties ,
  } = useClubSettings();
  const location = useLocation();
  const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState(25);
  const [currentSort, setCurrentSort] = useState<SortingState>([]);

  const createSort = function () {
    return currentSort?.[0]
      ? { field: currentSort[0]?.id, desc: currentSort[0]?.desc }
      : undefined;
  };
  const { data, isFetching } = useMembershipPausedReport({
    api,
    sort: createSort(),
    limit: pageSize,
    page: currentPage,
    tz: club.timezone,
  });

  const columnHelper = createColumnHelper<ReportMemberDTO>();
  const columnsDefinition = [
    columnHelper.accessor(
      (row) => {
        return {
          firstName: row.firstName,
          lastName: row.lastName,
          id: row.userMemberId,
        };
      },
      {
        id: "firstName",
        cell: (info) => {
          const memberName = `${info.getValue().firstName} ${
            info.getValue().lastName
          }`;
          return (
            <div>
              <LinkButton
                to={createMemberLink(
                  info.getValue().id,
                  UserMemberPage.Account,
                )}
              >
                {memberName}
              </LinkButton>
            </div>
          );
        },
        header: () => {
          return <div>Name</div>;
        },
        enableSorting: false,
        footer: () => <div>Total: {data?.totalElements}</div>,
      },
    ),
    columnHelper.accessor("mobileNumber", {
      cell: (info) => <div className="text-gray-600">{info.getValue()}</div>,
      header: "Contact No",
      enableSorting: false,
    }),

    columnHelper.accessor("email", {
      cell: (info) => <div className="text-gray-600">{info.getValue()}</div>,
      header: "Email",
      enableSorting: false,
    }),
    columnHelper.accessor("resumeDate", {
      cell: (info) => {
        const lastAttended = info.getValue();
        return lastAttended ? (
          <div className="text-gray-600">
            {moment(lastAttended, PARAMETER_DATE_FORMAT_WITHOUT_TZ).format(
              properties.dateFormat,
            )}
          </div>
        ) : null;
      },
      header: "Resume Date",
      enableSorting: false,
    }),
    columnHelper.accessor("pausedDate", {
      cell: (info) => {
        const lastAttended = info.getValue();
        return lastAttended ? (
          <div className="text-gray-600">
            {moment(lastAttended, PARAMETER_DATE_FORMAT_WITHOUT_TZ).format(
              properties.dateFormat,
            )}
          </div>
        ) : null;
      },
      header: "Date Paused",
      enableSorting: false,
    }),
  ];

  return (
    <Report
      title="Paused Memberships"
      description="List of all memberships in a paused status. Does not include memberships that are scheduled to be paused."
      close={() => {
        history.push({
          pathname: searchParams["backRoute"] as string,
          search: qs.stringify({
            dates: {
              startDate: searchParams["startDate"],
              endDate: searchParams["endDate"],
            },
          }),
        });
      }}
      table={
        <>
          <Table
            data={data?.content ?? []}
            columns={columnsDefinition}
            pageCount={data?.totalPages}
            pageIndex={data?.number}
            onSortingChange={setCurrentSort}
            sort={currentSort}
            isFetching={isFetching}
            pageSize={pageSize}
            rowClassName="h-14"
          />
          <div className="border-t-[0.063rem] border-gray-300">
            <ReportPagination
              pageCount={data?.totalPages as number}
              currentPage={data?.number as number}
              onPageChange={(newPage) => {
                setCurrentPage(newPage);
              }}
              pageSize={pageSize}
              setPageSize={(newValue) => {
                setPageSize(newValue);
                setCurrentPage(0);
              }}
            />
          </div>
        </>
      }
    />
  );
}
